import Box from '@mui/material/Box';

import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './QuillEditor.css';
import { ThemeDirection } from 'config';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Menu, MenuItem, Stack, TextField, Typography, ListItemIcon, ListItemText, Tooltip, useMediaQuery } from '@mui/material';
import {
  CloseOutlined,
  DeleteOutlined,
  VerticalAlignTopOutlined,
  VerticalAlignBottomOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  TableOutlined,
  MoreOutlined
} from '@ant-design/icons';
import ImageResize from './QuillImageResize';

const Quill = (ReactQuill as any).Quill;
Quill.register('modules/imageResize', ImageResize);

// Custom Blot for protected variables
const Embed = Quill.import('blots/embed');

let globalPreviewValues: any = {};
let globalIsPreview = false;

const resolveVariableValue = (placeholder: string, values: any) => {
  if (!values) return placeholder;

  // Handle special signature formats [!Sign.X.W=70.H=20] - Returning as is per user request
  if (placeholder.startsWith('[!')) {
    return placeholder;
  }

  // Handle standard {{Category.Field}}
  if (placeholder.startsWith('{{') && placeholder.endsWith('}}')) {
    const inner = placeholder.slice(2, -2); // Remove {{ and }}
    const dotIndex = inner.indexOf('.');
    if (dotIndex !== -1) {
      const categoryPart = inner.slice(0, dotIndex);
      const fieldPart = inner.slice(dotIndex + 1);

      // Find matching category in original names (ignoring spaces and dots)
      for (const [catName, fields] of Object.entries(values)) {
        if (catName.replace(/[\s.]/g, '') === categoryPart) {
          for (const [fieldName, val] of Object.entries(fields as any)) {
            if (fieldName.replace(/[\s()]/g, '') === fieldPart) {
              // Do not resolve actual Signature or Initials marks per user request
              // These usually end with "Signature" or "Initials" (e.g. "Signature 1 - Signature")
              if (catName === 'Signatures' && (fieldName.endsWith(' - Signature') || fieldName.endsWith(' - Initials'))) {
                return placeholder;
              }
              return (val !== null && val !== undefined && val !== '') ? val : '';
            }
          }
        }
      }
    }
  }


  // If it's a standard variable but wasn't found in the values object, 
  // return an empty string (especially useful for preview mode)
  if (placeholder.startsWith('{{') && placeholder.endsWith('}}')) {
    return '';
  }

  return placeholder;
};

class VariableBlot extends (Embed as any) {
  static create(value: string) {
    const node = super.create(value);
    node.setAttribute('data-value', value);
    node.setAttribute('contenteditable', 'false');
    node.classList.add('ql-variable');

    const preview = resolveVariableValue(value, globalPreviewValues);
    node.setAttribute('data-preview', preview);

    const normalizedValue = value.toLowerCase().replace(/\s/g, '');
    const isCustomField = normalizedValue.includes('customfield');
    node.setAttribute('data-is-custom-field', isCustomField ? 'true' : 'false');

    if (isCustomField) {
      const input = document.createElement('input');
      input.setAttribute('type', 'text');
      input.setAttribute('readonly', 'true');
      input.classList.add('ql-variable-textbox');

      const preview = resolveVariableValue(value, globalPreviewValues);
      input.value = (globalIsPreview ? (preview || '') : value) as string;

      node.appendChild(input);
    } else if (value.endsWith('.Logo}}')) {
      const img = document.createElement('img');
      img.classList.add('ql-variable-logo');

      // Placeholder for logo if value is empty
      const placeholderLogo = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23bfbfbf"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>';

      const preview = resolveVariableValue(value, globalPreviewValues);
      const hasPreview = typeof preview === 'string' && (preview.startsWith('data:image') || preview.startsWith('http'));
      img.src = hasPreview ? preview : placeholderLogo;

      // Default styles for logo variable in editor
      img.style.maxWidth = '150px';
      img.style.maxHeight = '100px';
      img.style.display = 'inline-block';
      img.style.verticalAlign = 'middle';
      img.style.border = '1px solid #f0f0f0';
      img.style.borderRadius = '4px';

      node.appendChild(img);
      // For Logos, we don't want the attr(data-preview) content to show up in preview mode CSS
      node.setAttribute('data-is-logo', 'true');
    } else {
      const preview = resolveVariableValue(value, globalPreviewValues);
      node.innerText = globalIsPreview ? preview : value;
    }
    return node;
  }

  static value(node: HTMLElement) {
    return node.getAttribute('data-value');
  }

  static formats(node: HTMLElement) {
    const formats: any = {};
    if (node.style.fontWeight === 'bold') formats.bold = true;
    if (node.style.fontStyle === 'italic') formats.italic = true;
    if (node.style.textDecoration.includes('underline')) formats.underline = true;
    if (node.style.textDecoration.includes('line-through')) formats.strike = true;
    if (node.style.color) formats.color = node.style.color;
    if (node.style.backgroundColor) formats.background = node.style.background || node.style.backgroundColor;
    if (node.style.fontSize) formats.size = node.getAttribute('data-size') || node.style.fontSize;
    if (node.style.fontFamily) formats.font = node.style.fontFamily;
    if (node.getAttribute('data-header')) formats.header = parseInt(node.getAttribute('data-header') || '0');
    if (node.style.textAlign) formats.align = node.style.textAlign;
    return formats;
  }

  format(name: string, value: any) {
    const styleMap: any = {
      bold: { prop: 'fontWeight', val: value ? 'bold' : 'normal' },
      italic: { prop: 'fontStyle', val: value ? 'italic' : 'normal' },
      underline: { prop: 'textDecoration', val: value ? 'underline' : 'none' },
      strike: { prop: 'textDecoration', val: value ? 'line-through' : 'none' },
      color: { prop: 'color', val: value },
      background: { prop: 'backgroundColor', val: value },
      font: { prop: 'fontFamily', val: value },
      align: { prop: 'textAlign', val: value },
    };

    if (styleMap[name]) {
      this.domNode.style[styleMap[name].prop] = styleMap[name].val;
    } else if (name === 'size') {
      if (value) {
        this.domNode.setAttribute('data-size', value);
        // Custom mapping for Quill sizes if needed, or just let CSS handle it
        const sizeMap: any = { 'small': '0.75em', 'large': '1.5em', 'huge': '2.5em' };
        this.domNode.style.fontSize = sizeMap[value] || value;
      } else {
        this.domNode.removeAttribute('data-size');
        this.domNode.style.fontSize = '';
      }
    } else if (name === 'header') {
      if (value) {
        this.domNode.setAttribute('data-header', value);
        const headerSizes: any = { '1': '2em', '2': '1.5em', '3': '1.17em', '4': '1em', '5': '0.83em', '6': '0.67em' };
        this.domNode.style.fontSize = headerSizes[value] || '';
        this.domNode.style.fontWeight = 'bold';
      } else {
        this.domNode.removeAttribute('data-header');
        this.domNode.style.fontSize = '';
        this.domNode.style.fontWeight = '';
      }
    } else {
      super.format(name, value);
    }
  }
}
(VariableBlot as any).blotName = 'variable';
(VariableBlot as any).tagName = 'span';
(VariableBlot as any).className = 'ql-variable';
Quill.register(VariableBlot);

interface Props {
  value?: string;
  editorMinHeight?: number | string;
  editorMaxHeight?: number | string;
  onChange?: (value: string, delta?: any, source?: string, editor?: any) => void;
  onChangeText?: (text: string) => void;
  placeholder?: string;
  disabled?: boolean;
  toolbarId?: string;
  isPreview?: boolean;
  previewValues?: Record<string, Record<string, string | undefined>>;
  showMoreButton?: boolean;
  hideImage?: boolean;
  hideTable?: boolean;
  hideLink?: boolean;
  hideVideo?: boolean;
  allowedVariables?: string[];
}

const myColors = [
  "#000000",
  "#e60000",
  "#ff9900",
  "#ffff00",
  "#008a00",
  "#0066cc",
  "#9933ff",
  "#ffffff",
  "#facccc",
  "#ffebcc",
  "#ffffcc",
  "#cce8cc",
  "#cce0f5",
  "#ebd6ff",
  "#bbbbbb",
  "#f06666",
  "#ffc266",
  "#ffff66",
  "#66b966",
  "#66a3e0",
  "#c285ff",
  "#888888",
  "#a10000",
  "#b26b00",
  "#b2b200",
  "#006100",
  "#0047b2",
  "#6b24b2",
  "#444444",
  "#5c0000",
  "#663d00",
  "#666600",
  "#003700",
  "#002966",
  "#3d1466",
];

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  'indent',
  "link",
  "color",
  "image",
  "video",
  "background",
  "script",
  "align",
  'table',
  'variable'
];

const TOOLBAR_GROUPS = [
  { id: 'font', minWidth: 105 },
  { id: 'size', minWidth: 95 },
  { id: 'formatting', minWidth: 140 },
  { id: 'color', minWidth: 60 },
  { id: 'script', minWidth: 56 },
  { id: 'list', minWidth: 110 },
  { id: 'align', minWidth: 36 },
  { id: 'insert', minWidth: 84 },
  { id: 'table', minWidth: 36 },
  { id: 'clean', minWidth: 28 }
] as const;

type GroupId = typeof TOOLBAR_GROUPS[number]['id'];

const QuillEditor = forwardRef(({
  value,
  disabled = false,
  placeholder = '',
  editorMinHeight = 120,
  editorMaxHeight = 250,
  onChange,
  onChangeText,
  toolbarId,
  isPreview = false,
  showMoreButton = true,
  previewValues,
  hideImage = false,
  hideTable = false,
  hideLink = false,
  hideVideo = false,
  allowedVariables
}: Props, ref) => {
  const allowedVariablesRef = useRef(allowedVariables);
  useEffect(() => {
    allowedVariablesRef.current = allowedVariables;
  }, [allowedVariables]);

  const isAllowedVariable = (token: string) => {
    const list = allowedVariablesRef.current;
    if (!list || !Array.isArray(list)) {
      return true;
    }
    if (token.startsWith('[!')) {
      return true;
    }
    return list.includes(token) || list.includes(token.trim());
  };
  const internalToolbarId = useMemo(() => `quill-toolbar-${Math.random().toString(36).substr(2, 9)}`, []);
  const activeToolbarId = toolbarId || internalToolbarId;
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const toolbarRef = useRef<any>(null)
  const hiddenToolbarRef = useRef<HTMLDivElement>(null);

  const activeToolbarGroups = useMemo(() => {
    return TOOLBAR_GROUPS.filter(g => {
      if (g.id === 'table' && hideTable) return false;
      if (g.id === 'insert' && hideImage && hideLink && hideVideo) return false;
      return true;
    }).map(g => {
      if (g.id === 'insert') {
        let width = 84;
        if (hideLink) width -= 28;
        if (hideImage) width -= 28;
        if (hideVideo) width -= 28;
        return { ...g, minWidth: Math.max(width, 0) };
      }
      return g;
    });
  }, [hideTable, hideImage, hideLink, hideVideo]);

  const [visibleGroups, setVisibleGroups] = useState<Set<GroupId>>(
    new Set(activeToolbarGroups.map(g => g.id))
  );
  const [hasOverflow, setHasOverflow] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!showMoreButton) return

    const el = toolbarRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      const HEADING_WIDTH = 115;
      const MORE_BTN_WIDTH = 32;
      const PADDING = 16;
      const GROUP_SPACING = 16;
      const totalGroupsWidth = activeToolbarGroups.reduce(
        (sum, group, index) => sum + group.minWidth + (index < activeToolbarGroups.length - 1 ? GROUP_SPACING : 0),
        0
      );
      const availableWithoutBtn = entry.contentRect.width - HEADING_WIDTH - PADDING;

      if (availableWithoutBtn >= totalGroupsWidth) {
        setVisibleGroups(new Set(activeToolbarGroups.map(g => g.id)));
        setHasOverflow(false);
        return;
      }

      let available = availableWithoutBtn - MORE_BTN_WIDTH;

      const newVisible = new Set<GroupId>();
      let overflow = false;

      for (const group of activeToolbarGroups) {
        const groupWidth = group.minWidth + GROUP_SPACING;
        if (available >= groupWidth) {
          newVisible.add(group.id);
          available -= groupWidth;
        } else {
          overflow = true;
        }
      }

      setVisibleGroups(newVisible);
      setHasOverflow(overflow);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [showMoreButton, activeToolbarGroups]);

  const handleMoreOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setMoreMenuAnchor(e.currentTarget);

    requestAnimationFrame(() => {
      const menuEl = hiddenToolbarRef.current;
      const menuWidth = menuEl?.offsetWidth ?? 0;
      const menuHeight = menuEl?.offsetHeight ?? 0;

      let left = rect.right - menuWidth;
      let top = rect.bottom + 4;

      const margin = 48;
      if (left < margin) {
        left = margin;
      }
      if (left + menuWidth > window.innerWidth - margin) {
        left = window.innerWidth - menuWidth - margin;
      }
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 4;
      }

      setMenuPosition({ top, left });
    });
  };

  const handleMoreClose = () => {
    setMoreMenuAnchor(null);
    setMenuPosition(null);
  };

  const quillRef = useRef<any>(null);
  const lastSelectionRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    getEditor: () => quillRef.current?.getEditor(),
    getContents: () => {
      const quill = quillRef.current?.getEditor();
      if (quill) {
        return quill.root.innerHTML;
      }
      return '';
    },
    insertValue: (val: string) => {
      const quill = quillRef.current?.getEditor();
      if (quill) {
        const range = quill.getSelection() || lastSelectionRef.current;
        let index = range ? range.index : quill.getLength() - 1;
        if (index < 0) index = 0;

        // Get current formatting at cursor
        const format = range ? quill.getFormat(range) : {};

        quill.insertEmbed(index, 'variable', val, 'api');

        // Apply same formatting to variable blot
        if (Object.keys(format).length > 0) {
          quill.formatText(index, 1, format, 'api');
        }

        const nextIndex = index + 1;
        quill.setSelection(nextIndex, 0, 'user');
        lastSelectionRef.current = { index: nextIndex, length: 0 };
        quill.focus();
      }
    }
  }));

  const [openTableDialog, setOpenTableDialog] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  const [selectionIndex, setSelectionIndex] = useState<number | null>(null);
  const [editorReady, setEditorReady] = useState(false);

  const conversionTimeoutRef = useRef<any>(null);
  const onChangeTextTimeoutRef = useRef<any>(null);

  // Use a ref to store the latest onChangeText to avoid closure issues in debounced calls
  const onChangeTextRef = useRef(onChangeText);
  useEffect(() => {
    onChangeTextRef.current = onChangeText;
  }, [onChangeText]);

  const handleOpenTableDialog = () => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      // Capture range even if the editor isn't currently focused
      const range = quill.getSelection(true);
      setSelectionIndex(range ? range.index : 0);

      // Explicitly blur to prevent focus conflicts with dialog's aria-hidden backdrop
      // This solves the MUI "Blocked aria-hidden" console error.
      quill.blur();
    }
    setOpenTableDialog(true);
  };

  const handleCloseTableDialog = () => {
    setOpenTableDialog(false);
  };

  const generateTableHTML = (rows: number, cols: number) => {
    let html = '<table border="1" style="border-collapse: collapse; width: 100%;"><tbody>';
    for (let i = 0; i < rows; i++) {
      html += '<tr>';
      for (let j = 0; j < cols; j++) {
        html += '<td style="border: 1px solid #000; padding: 5px; min-width: 50px;"><br></td>';
      }
      html += '</tr>';
    }
    html += '</tbody></table><p><br></p>';
    return html;
  };

  const handleInsertTable = () => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      // 1. Snapshot counts
      const tRows = Math.max(1, parseInt(tableRows as any) || 3);
      const tCols = Math.max(1, parseInt(tableCols as any) || 3);

      // 2. Regain focus 
      quill.focus();

      // 3. Determine index (default 0 for empty editor)
      const index = (selectionIndex !== null && selectionIndex >= 0) ? selectionIndex : 0;

      const tableModule = quill.getModule('table');
      // Preferred Quill 2.0 insertion with focus snapping
      if (tableModule && typeof tableModule.insertTable === 'function') {
        try {
          tableModule.insertTable(tRows, tCols);
          quill.update('user');
          // Silky smooth focus snap into the first cell
          setTimeout(() => {
            quill.setSelection(index + 1, 0, 'user');
          }, 0);
        } catch (err) {
          console.warn('Native insertTable failed, using fallback:', err);
          const tableHTML = generateTableHTML(tRows, tCols);
          quill.clipboard.dangerouslyPasteHTML(index, tableHTML, 'user');
          setTimeout(() => quill.setSelection(index + 1, 0, 'user'), 0);
        }
      } else {
        const tableHTML = generateTableHTML(tRows, tCols);
        quill.clipboard.dangerouslyPasteHTML(index, tableHTML, 'user');
        setTimeout(() => quill.setSelection(index + 1, 0, 'user'), 0);
      }
    }
    handleCloseTableDialog();
  };

  const handleTableAction = (action: string) => {
    const quill = quillRef.current?.getEditor();
    const tableModule = quill?.getModule('table');
    if (tableModule) {
      switch (action) {
        case 'insert-row-above': tableModule.insertRowAbove(); break;
        case 'insert-row-below': tableModule.insertRowBelow(); break;
        case 'insert-column-left': tableModule.insertColumnLeft(); break;
        case 'insert-column-right': tableModule.insertColumnRight(); break;
        case 'delete-row': tableModule.deleteRow(); break;
        case 'delete-column': tableModule.deleteColumn(); break;
        case 'delete-table': tableModule.deleteTable(); break;
      }
      quill.update('user');
    }
    setContextMenu(null);
  };


  const handleImage = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = () => {
      const file = input.files ? input.files[0] : null;
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = e.target?.result;
          if (img) {
            const quill = quillRef.current?.getEditor();
            if (quill) {
              const range = quill.getSelection(true);
              const index = range ? range.index : quill.getLength();
              quill.insertEmbed(index, 'image', img);
            }
          }
        };
        reader.readAsDataURL(file);
      }
    };
  };

  const onChangeTimeoutRef = useRef<any>(null);

  const modules = useMemo(() => ({
    toolbar: {
      container: `#${activeToolbarId}`,
      handlers: {
        insertTable: handleOpenTableDialog,
        image: handleImage,
      }
    },
    imageResize: {},
    table: true,
    clipboard: {
      matchers: [
        ['P', (node: any, delta: any) => {
          const hasMedia = node.querySelector('img, iframe, video, .ql-variable');
          if (!hasMedia && (node.innerHTML === '' || node.innerHTML === '<br>' || !node.textContent?.trim())) {
            const Delta = Quill.import('delta');

            return new Delta().insert('\n', { blank: true });
          }
          return delta;
        }],
        [Node.TEXT_NODE, (node: any, delta: any) => {
          const regex = /{{[a-zA-Z0-9.\-_:]+}}|\[!(Sign|Initial_Sign|Input_Text)\.\d+(\.\d+)?\.W=\d+\.H=\d+\]/g;
          if (typeof node.data !== 'string') return delta;
          const matches = node.data.match(regex);
          if (matches && delta.ops) {
            const validMatches = matches.filter((m: string) => isAllowedVariable(m));
            if (validMatches.length === 0) return delta;

            const newDelta = new (Quill.import('delta'))();
            let lastIndex = 0;
            validMatches.forEach((match: string) => {
              const index = node.data.indexOf(match, lastIndex);
              if (index !== -1) {
                newDelta.insert(node.data.slice(lastIndex, index));
                newDelta.insert({ variable: match });
                lastIndex = index + match.length;
              }
            });
            newDelta.insert(node.data.slice(lastIndex));
            return newDelta;
          }
          return delta;
        }]
      ]
    }
  }), [activeToolbarId]);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const dropHandler = (e: DragEvent) => {
        // Only handle if it's a variable drag-drop
        const data = e.dataTransfer?.getData('text/plain');
        const isVariable = (data && data.startsWith('{{') && data.endsWith('}}')) ||
          (data && data.startsWith('[!') && data.endsWith(']'));
        if (isVariable) {
          e.preventDefault();

          let index = quill.getLength();
          let foundHit = false;

          // 1. Try to detect direct hit on a variable (smart detection for "between variables")
          const elementAtPoint = document.elementFromPoint(e.clientX, e.clientY);
          if (elementAtPoint) {
            const variableNode = elementAtPoint.closest('.ql-variable');
            if (variableNode) {
              const blot = Quill.find(variableNode);
              if (blot) {
                const blotIndex = quill.getIndex(blot);
                if (blotIndex !== -1) {
                  const rect = variableNode.getBoundingClientRect();
                  const isAfter = (e.clientX - rect.left) > (rect.width / 2);
                  index = blotIndex + (isAfter ? 1 : 0);
                  foundHit = true;
                }
              }
            } else if (elementAtPoint.closest('.ql-editor')) {
              // If we dropped on whitespace or text, try to find the index via caret
              // (Already handled below)
            }
          }

          // 2. Fallback to coordinate-to-index translation using caret mapping
          if (!foundHit) {
            if (document.caretRangeFromPoint) {
              const range = document.caretRangeFromPoint(e.clientX, e.clientY);
              if (range) {
                const node = range.startContainer;
                const offset = range.startOffset;
                const blot = Quill.find(node);

                if (blot) {
                  const blotIndex = quill.getIndex(blot);
                  if (blotIndex !== -1) {
                    if (node.nodeType === Node.TEXT_NODE) {
                      index = blotIndex + offset;
                    } else {
                      let charOffset = 0;
                      for (let i = 0; i < offset && i < node.childNodes.length; i++) {
                        const childBlot = Quill.find(node.childNodes[i]);
                        if (childBlot) charOffset += childBlot.length();
                      }
                      index = blotIndex + charOffset;
                    }
                  }
                }
              }
            } else if ((document as any).caretPositionFromPoint) {
              // ... existing Firefox fallback ...
              const position = (document as any).caretPositionFromPoint(e.clientX, e.clientY);
              if (position) {
                const node = position.offsetNode;
                const offset = position.offset;
                const blot = Quill.find(node);
                if (blot) {
                  const blotIndex = quill.getIndex(blot);
                  if (blotIndex !== -1) {
                    if (node.nodeType === Node.TEXT_NODE) {
                      index = blotIndex + offset;
                    } else {
                      let charOffset = 0;
                      for (let i = 0; i < offset && i < node.childNodes.length; i++) {
                        const childBlot = Quill.find(node.childNodes[i]);
                        if (childBlot) charOffset += childBlot.length();
                      }
                      index = blotIndex + charOffset;
                    }
                  }
                }
              }
            }
          }

          // Ensure index doesn't go beyond length - 1 (before trailing newline) if content exists
          const docLength = quill.getLength();
          if (index >= docLength && docLength > 0) {
            index = docLength - 1;
          }

          // Inherit format from dropping position
          const format = quill.getFormat(index, 0);

          quill.insertEmbed(index, 'variable', data, 'api');

          if (Object.keys(format).length > 0) {
            quill.formatText(index, 1, format, 'api');
          }

          quill.setSelection(index + 1);
          quill.focus();
        }
      };

      const dragOverHandler = (e: DragEvent) => {
        e.preventDefault();
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'copy';
        }
      };

      quill.root.addEventListener('drop', dropHandler);
      quill.root.addEventListener('dragover', dragOverHandler);

      const scanForVariables = () => {
        const currentQuill = quillRef.current?.getEditor();
        if (!currentQuill) return;

        const text = currentQuill.getText();
        const regex = /{{[a-zA-Z0-9.\-_:]+}}|\[!(Sign|Initial_Sign|Input_Text)\.\d+(\.\d+)?\.W=\d+\.H=\d+\]/g;
        let match;
        const matches = [];

        while ((match = regex.exec(text)) !== null) {
          const tokenValue = match[0];
          if (!isAllowedVariable(tokenValue)) {
            continue;
          }
          const index = match.index;
          const [blot] = currentQuill.getLeaf(index);
          if (!blot || !(blot as any).statics || (blot as any).statics.blotName !== 'variable') {
            matches.push({
              index: match.index,
              length: tokenValue.length,
              value: tokenValue
            });
          }
        }

        if (matches.length > 0) {
          // Apply matches in reverse order to keep indices valid
          for (let i = matches.length - 1; i >= 0; i--) {
            const m = matches[i];
            const currentFormat = currentQuill.getFormat(m.index, m.length);
            currentQuill.deleteText(m.index, m.length, 'api');
            currentQuill.insertEmbed(m.index, 'variable', m.value, 'api');
            if (Object.keys(currentFormat).length > 0) {
              currentQuill.formatText(m.index, 1, currentFormat, 'api');
            }
          }

          // Restore focus and cursor
          const range = currentQuill.getSelection();
          if (range) {
            currentQuill.setSelection(range.index, 0, 'api');
          }
        }
      };

      const textChangeHandler = (delta: any, oldDelta: any, source: string) => {
        if (source === 'user') {
          if (conversionTimeoutRef.current) clearTimeout(conversionTimeoutRef.current);
          conversionTimeoutRef.current = setTimeout(scanForVariables, 500);

          // Auto-scroll only when cursor is near end
          requestAnimationFrame(() => {
            const q = quillRef.current?.getEditor();
            if (!q) return;

            const editorEl = q.root;
            const selection = q.getSelection();
            if (!selection) return;

            const docLength = q.getLength();
            const isNearEnd = selection.index >= docLength - 2;

            if (isNearEnd) {
              editorEl.scrollTop = editorEl.scrollHeight;
            }
          });
        }
      };

      quill.on('text-change', textChangeHandler);

      let selectedVariableNode: Element | null = null;

      const clearVariableSelection = () => {
        if (selectedVariableNode) {
          selectedVariableNode.classList.remove('ql-variable-selected');
          selectedVariableNode = null;
        }
      };

      // Handle variable mousedown to select them for easier formatting
      const mousedownHandler = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const variableNode = target.closest('.ql-variable');
        if (variableNode) {
          const blot = Quill.find(variableNode);
          if (blot) {
            e.preventDefault();
            // Clear previous highlight
            clearVariableSelection();
            // Highlight this variable
            variableNode.classList.add('ql-variable-selected');
            selectedVariableNode = variableNode;
            const index = quill.getIndex(blot);
            // Focus and select with 'user' so toolbar reacts
            quill.focus();
            quill.setSelection(index, 1, 'user');
          }
        } else {
          clearVariableSelection();
        }
      };
      quill.root.addEventListener('mousedown', mousedownHandler);

      const handleContextMenu = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const cell = target.closest('td, th');
        if (cell && !disabled) {
          e.preventDefault();

          // Try to set selection to the cell area to make table module aware of the target
          const blot = Quill.find(cell);
          if (blot) {
            const index = quill.getIndex(blot);
            quill.setSelection(index, 0, 'user');
          }

          setContextMenu({
            mouseX: e.clientX - 2,
            mouseY: e.clientY - 4,
          });
        }
      };
      quill.root.addEventListener('contextmenu', handleContextMenu);

      // Clear highlight when selection moves away from a variable
      const selectionChangeHandler = (range: any) => {
        if (range) {
          lastSelectionRef.current = range;
        }
        if (!range) {
          clearVariableSelection();
          return;
        }
        if (range.length !== 1) {
          clearVariableSelection();
          return;
        }
        // Check if selected blot is still a variable
        const [blot] = quill.getLeaf(range.index);
        if (!blot || (blot as any).statics?.blotName !== 'variable') {
          clearVariableSelection();
        }
      };
      quill.on('selection-change', selectionChangeHandler);

      return () => {
        if (conversionTimeoutRef.current) clearTimeout(conversionTimeoutRef.current);
        if (onChangeTextTimeoutRef.current) clearTimeout(onChangeTextTimeoutRef.current);
        if (onChangeTimeoutRef.current) clearTimeout(onChangeTimeoutRef.current);
        quill.root.removeEventListener('drop', dropHandler);
        quill.root.removeEventListener('dragover', dragOverHandler);
        quill.off('text-change', textChangeHandler);
        quill.root.removeEventListener('mousedown', mousedownHandler);
        quill.root.removeEventListener('contextmenu', handleContextMenu);
        quill.off('selection-change', selectionChangeHandler);
      };
    }
  }, [editorReady]);

  // Check if editor is ready to attach listeners
  useEffect(() => {
    if (!editorReady && quillRef.current?.getEditor()) {
      setEditorReady(true);
    }
  });

  useEffect(() => {
    globalPreviewValues = previewValues || {};
    globalIsPreview = isPreview || false;

    const quill = quillRef.current?.getEditor();
    if (quill) {
      const variables = quill.root.querySelectorAll('.ql-variable');
      variables.forEach((node: any) => {
        const value = node.getAttribute('data-value');
        if (value) {
          const preview = resolveVariableValue(value, globalPreviewValues);
          node.setAttribute('data-preview', preview);

          // Update image source if it's a logo
          const normalizedValue = value.toLowerCase().replace(/\s/g, '');
          if (normalizedValue.includes('customfield1') || normalizedValue.includes('customfield2') || normalizedValue.includes('customfield3')) {
            const input = node.querySelector('input.ql-variable-textbox');
            if (input) {
              const targetValue = isPreview ? (preview || '') : value;
              if (input.value !== targetValue) {
                input.value = targetValue;
              }
            }
          } else if (value.endsWith('.Logo}}')) {
            const img = node.querySelector('img.ql-variable-logo');
            if (img) {
              const placeholderLogo = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23bfbfbf"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>';
              const hasPreview = typeof preview === 'string' && (preview.startsWith('data:image') || preview.startsWith('http'));
              img.src = hasPreview ? preview : placeholderLogo;
            }
          } else {
            // Swap text between placeholder and preview value
            if (isPreview) {
              if (node.innerText !== preview) {
                node.innerText = preview;
              }
            } else {
              if (node.innerText !== value) {
                node.innerText = value;
              }
            }
          }
        }
      });
    }
  }, [previewValues, quillRef, isPreview]);

  return (
    <Box
      sx={(theme) => ({
        '& .ql-toolbar.ql-snow': {
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          bgcolor: '#f8f9fa',
          p: '6px 10px',
          borderRadius: '4px 4px 0 0',
          border: '1px solid',
          borderColor: 'divider',
          borderBottom: 'none',
          width: '100%',
          overflow: 'hidden',
          '& .ql-formats': {
            display: 'flex',
            alignItems: 'center',
            mr: '8px !important',
            borderRight: '1px solid',
            borderColor: 'grey.300',
            pr: '8px',
            '&:last-child': { borderRight: 'none', pr: 0, mr: 0 }
          },
          '& button, & .ql-picker': {
            border: 'none',
            '&:hover': { color: 'primary.main' }
          },
          '& .ql-stroke': { stroke: '#444' },
          '& .ql-fill': { fill: '#444' },
          '& .ql-picker-label': { fontSize: '13px', fontWeight: 500, color: '#444' },
          '& .ql-picker-item': { fontSize: '13px' }
        },
        '& .quill': {
          bgcolor: 'secondary.lighter',
          ...theme.applyStyles('dark', { bgcolor: 'secondary.main' }),
          borderRadius: '4px',
          '& .ql-container': {
            bgcolor: 'background.paper',
            ...theme.applyStyles('dark', { bgcolor: 'background.default' }),
            borderColor: `${theme.palette.divider} !important`,
            borderBottomLeftRadius: '4px',
            borderBottomRightRadius: '4px',
            '& .ql-editor': { minHeight: editorMinHeight, maxHeight: editorMaxHeight, minWidth: '300px' },
            '& .ql-editor.ql-blank::before': {
              fontStyle: 'normal',
              color: theme.palette.text.secondary
            }
          },
          ...(theme.direction === ThemeDirection.RTL && {
            '& .ql-snow .ql-picker:not(.ql-color-picker):not(.ql-icon-picker) svg': {
              right: '0%',
              left: 'inherit'
            }
          })
        }
      })}
      className={isPreview ? 'ql-preview' : ''}
    >
      {!toolbarId && (
        <Box id={activeToolbarId} className="ql-toolbar ql-snow" ref={toolbarRef}>
          <Stack direction="row" spacing={0} flexWrap="nowrap" useFlexGap alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
            <span className="ql-formats">
              <select className="ql-header" defaultValue="">
                <option value="1">Heading 1</option>
                <option value="2">Heading 2</option>
                <option value="3">Heading 3</option>
                <option value="4">Heading 4</option>
                <option value="5">Heading 5</option>
                <option value="6">Heading 6</option>
              </select>
            </span>
            <span className="ql-formats" style={{ display: visibleGroups.has('font') ? 'inline-flex' : 'none' }}>
              <select className="ql-font" defaultValue="">
                <option value="">Sans Serif</option>
                <option value="serif">Serif</option>
                <option value="monospace">Monospace</option>
              </select>
            </span>

            <span className="ql-formats" style={{ display: visibleGroups.has('size') ? 'inline-flex' : 'none' }}>
              <select className="ql-size" defaultValue="">
                <option value="small">Small</option>
                <option value="">Normal</option>
                <option value="large">Large</option>
                <option value="huge">Huge</option>
              </select>
            </span>

            <span className="ql-formats" style={{ display: visibleGroups.has('formatting') ? 'inline-flex' : 'none' }}>
              <button className="ql-bold" title="Bold" />
              <button className="ql-italic" title="Italic" />
              <button className="ql-underline" title="Underline" />
              <button className="ql-strike" title="Strike" />
              <button className="ql-blockquote" title="Blockquote" />
            </span>

            <span className="ql-formats" style={{ display: visibleGroups.has('color') ? 'inline-flex' : 'none' }}>
              <select className="ql-color">
                <option value="#000000" />
                <option value="#e60000" />
                <option value="#ff9900" />
                <option value="#ffff00" />
                <option value="#008a00" />
                <option value="#0066cc" />
                <option value="#9933ff" />
                <option value="#ffffff" />
                <option value="#facccc" />
                <option value="#ffebcc" />
                <option value="#ffffcc" />
                <option value="#cce8cc" />
                <option value="#cce0f5" />
                <option value="#ebd6ff" />
                <option value="#bbbbbb" />
                <option value="#f06666" />
                <option value="#ffc266" />
                <option value="#ffff66" />
                <option value="#66b966" />
                <option value="#66a3e0" />
                <option value="#c285ff" />
                <option value="#888888" />
                <option value="#a10000" />
                <option value="#b26b00" />
                <option value="#b2b200" />
                <option value="#006100" />
                <option value="#0047b2" />
                <option value="#6b24b2" />
                <option value="#444444" />
                <option value="#5c0000" />
                <option value="#663d00" />
                <option value="#666600" />
                <option value="#003700" />
                <option value="#002966" />
                <option value="#3d1466" />
              </select>
              <select className="ql-background">
                <option value="#000000" />
                <option value="#e60000" />
                <option value="#ff9900" />
                <option value="#ffff00" />
                <option value="#008a00" />
                <option value="#0066cc" />
                <option value="#9933ff" />
                <option value="#ffffff" />
                <option value="#facccc" />
                <option value="#ffebcc" />
                <option value="#ffffcc" />
                <option value="#cce8cc" />
                <option value="#cce0f5" />
                <option value="#ebd6ff" />
                <option value="#bbbbbb" />
                <option value="#f06666" />
                <option value="#ffc266" />
                <option value="#ffff66" />
                <option value="#66b966" />
                <option value="#66a3e0" />
                <option value="#c285ff" />
                <option value="#888888" />
                <option value="#a10000" />
                <option value="#b26b00" />
                <option value="#b2b200" />
                <option value="#006100" />
                <option value="#0047b2" />
                <option value="#6b24b2" />
                <option value="#444444" />
                <option value="#5c0000" />
                <option value="#663d00" />
                <option value="#666600" />
                <option value="#003700" />
                <option value="#002966" />
                <option value="#3d1466" />
              </select>
            </span>

            <span className="ql-formats" style={{ display: visibleGroups.has('script') ? 'inline-flex' : 'none' }}>
              <button className="ql-script" value="sub" />
              <button className="ql-script" value="super" />
            </span>

            <span className="ql-formats" style={{ display: visibleGroups.has('list') ? 'inline-flex' : 'none' }}>
              <button className="ql-list" value="ordered" />
              <button className="ql-list" value="bullet" />
              <button className="ql-indent" value="-1" />
              <button className="ql-indent" value="+1" />
            </span>

            <span className="ql-formats" style={{ display: visibleGroups.has('align') ? 'inline-flex' : 'none' }}>
              <select className="ql-align" />
            </span>

            <span className="ql-formats" style={{ display: (visibleGroups.has('insert') && (!hideLink || !hideImage || !hideVideo)) ? 'inline-flex' : 'none' }}>
              {!hideLink && <button className="ql-link" />}
              {!hideImage && <button className="ql-image" />}
              {!hideVideo && <button className="ql-video" />}
            </span>

            <span className="ql-formats" style={{ display: (visibleGroups.has('table') && !hideTable) ? 'inline-flex' : 'none' }}>
              <button className="ql-insertTable" title="Insert Table" />
            </span>

            <span className="ql-formats" style={{ display: visibleGroups.has('clean') ? 'inline-flex' : 'none' }}>
              <button className="ql-clean" title="Clear Formatting" />
            </span>

            {hasOverflow && showMoreButton && (
              <span className="ql-formats" style={{ marginLeft: 'auto', flexShrink: 0, borderRight: 'none', paddingRight: 0, marginRight: 0 }}>
                <IconButton
                  disabled={disabled}
                  onClick={handleMoreOpen}
                  sx={{
                    border: (theme) => `1px solid ${theme.palette.grey[300]} !important`,
                    borderRadius: '6px',
                    padding: '2px',
                    width: '24px !important',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: (theme) => theme.palette.grey[300],
                      backgroundColor: (theme) => theme.palette.grey[200]
                    }
                  }}
                >
                  <MoreOutlined style={{ fontSize: 13 }} />
                </IconButton>
              </span>
            )}
          </Stack>

          <Box
            ref={hiddenToolbarRef}
            className="ql-toolbar ql-snow"
            sx={{
              display: hasOverflow && Boolean(moreMenuAnchor) ? 'flex' : 'none !important',
              flexWrap: 'wrap',
              width: 'max-content !important',
              maxWidth: 'calc(100vw - 96px)',
              minWidth: 'unset',
              position: 'fixed',
              zIndex: 1300,
              top: menuPosition?.top ?? -9999,
              left: menuPosition?.left ?? -9999,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              boxShadow: '0px 5px 15px rgba(0,0,0,0.12)',
              p: 1,
            }}
          >
            {Boolean(moreMenuAnchor) && (
              <Box
                sx={{ position: 'fixed', inset: 0, zIndex: -1 }}
                onClick={handleMoreClose}
              />
            )}
            <span className="ql-formats" style={{ display: !visibleGroups.has('font') ? 'flex' : 'none' }}>
              <select className="ql-font" defaultValue="">
                <option value="">Sans Serif</option>
                <option value="serif">Serif</option>
                <option value="monospace">Monospace</option>
              </select>
            </span>

            <span className="ql-formats" style={{ display: !visibleGroups.has('size') ? 'flex' : 'none' }}>
              <select className="ql-size" defaultValue="">
                <option value="small">Small</option>
                <option value="">Normal</option>
                <option value="large">Large</option>
                <option value="huge">Huge</option>
              </select>
            </span>
            <span className="ql-formats" style={{ display: !visibleGroups.has('formatting') ? 'flex' : 'none' }}>
              <button className="ql-bold" title="Bold" />
              <button className="ql-italic" title="Italic" />
              <button className="ql-underline" title="Underline" />
              <button className="ql-strike" title="Strike" />
              <button className="ql-blockquote" title="Blockquote" />
            </span>
            <span className="ql-formats" style={{ display: !visibleGroups.has('color') ? 'flex' : 'none' }}>
              <select className="ql-color">
                <option value="#000000" />
                <option value="#e60000" />
                <option value="#ff9900" />
                <option value="#ffff00" />
                <option value="#008a00" />
                <option value="#0066cc" />
                <option value="#9933ff" />
                <option value="#ffffff" />
                <option value="#facccc" />
                <option value="#ffebcc" />
                <option value="#ffffcc" />
                <option value="#cce8cc" />
                <option value="#cce0f5" />
                <option value="#ebd6ff" />
                <option value="#bbbbbb" />
                <option value="#f06666" />
                <option value="#ffc266" />
                <option value="#ffff66" />
                <option value="#66b966" />
                <option value="#66a3e0" />
                <option value="#c285ff" />
                <option value="#888888" />
                <option value="#a10000" />
                <option value="#b26b00" />
                <option value="#b2b200" />
                <option value="#006100" />
                <option value="#0047b2" />
                <option value="#6b24b2" />
                <option value="#444444" />
                <option value="#5c0000" />
                <option value="#663d00" />
                <option value="#666600" />
                <option value="#003700" />
                <option value="#002966" />
                <option value="#3d1466" />
              </select>
              <select className="ql-background">
                <option value="#000000" />
                <option value="#e60000" />
                <option value="#ff9900" />
                <option value="#ffff00" />
                <option value="#008a00" />
                <option value="#0066cc" />
                <option value="#9933ff" />
                <option value="#ffffff" />
                <option value="#facccc" />
                <option value="#ffebcc" />
                <option value="#ffffcc" />
                <option value="#cce8cc" />
                <option value="#cce0f5" />
                <option value="#ebd6ff" />
                <option value="#bbbbbb" />
                <option value="#f06666" />
                <option value="#ffc266" />
                <option value="#ffff66" />
                <option value="#66b966" />
                <option value="#66a3e0" />
                <option value="#c285ff" />
                <option value="#888888" />
                <option value="#a10000" />
                <option value="#b26b00" />
                <option value="#b2b200" />
                <option value="#006100" />
                <option value="#0047b2" />
                <option value="#6b24b2" />
                <option value="#444444" />
                <option value="#5c0000" />
                <option value="#663d00" />
                <option value="#666600" />
                <option value="#003700" />
                <option value="#002966" />
                <option value="#3d1466" />
              </select>
            </span>
            <span className="ql-formats" style={{ display: !visibleGroups.has('script') ? 'flex' : 'none' }}>
              <button className="ql-script" value="sub" />
              <button className="ql-script" value="super" />
            </span>
            <span className="ql-formats" style={{ display: !visibleGroups.has('list') ? 'flex' : 'none' }}>
              <button className="ql-list" value="ordered" />
              <button className="ql-list" value="bullet" />
              <button className="ql-indent" value="-1" />
              <button className="ql-indent" value="+1" />
            </span>
            <span className="ql-formats" style={{ display: !visibleGroups.has('align') ? 'flex' : 'none' }}>
              <select className="ql-align" />
            </span>
            <span className="ql-formats" style={{ display: !visibleGroups.has('insert') && (!hideLink || !hideImage || !hideVideo) ? 'flex' : 'none' }}>
              {!hideLink && <button className="ql-link" />}
              {!hideImage && <button className="ql-image" />}
              {!hideVideo && <button className="ql-video" />}
            </span>
            <span className="ql-formats" style={{ display: !visibleGroups.has('table') && !hideTable ? 'flex' : 'none' }}>
              <button className="ql-insertTable" />
            </span>
            <span className="ql-formats" style={{ display: !visibleGroups.has('clean') ? 'flex' : 'none' }}>
              <button className="ql-clean" />
            </span>
          </Box>
        </Box >
      )}
      <ReactQuill
        ref={quillRef}
        readOnly={disabled}
        {...(value !== undefined && { value })}
        {...(onChange && {
          onChange: (content, delta, source, editor) => {
            // Debounce the main onChange to break the React re-render loop
            if (onChangeTimeoutRef.current) clearTimeout(onChangeTimeoutRef.current);
            onChangeTimeoutRef.current = setTimeout(() => {
              onChange?.(content, delta, source, editor);
            }, 300);

            if (onChangeTextRef.current) {
              if (onChangeTextTimeoutRef.current) clearTimeout(onChangeTextTimeoutRef.current);
              onChangeTextTimeoutRef.current = setTimeout(() => {
                onChangeTextRef.current?.(editor.getText());
              }, 500);
            }
          }
        })}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
      />

      <Dialog open={openTableDialog} onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          handleCloseTableDialog();
        }
      }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            <>
              <Typography component="span" sx={{ color: 'primary.main' }}>
                Insert
              </Typography>{' '}
              Table                                </>

          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseTableDialog}
            sx={{
              color: (theme) => theme.palette.primary.main
            }}
          >
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField
              label="Rows"
              type="number"
              value={tableRows}
              onChange={(e) =>
                setTableRows(
                  Math.min(99, Math.max(0, Number(e.target.value)))
                )
              }
              InputProps={{
                inputProps: { min: 0, max: 99 }
              }}
            />

            <TextField
              label="Columns"
              type="number"
              value={tableCols}
              onChange={(e) =>
                setTableCols(
                  Math.min(99, Math.max(0, Number(e.target.value)))
                )
              }
              InputProps={{
                inputProps: { min: 0, max: 99 }
              }}
            />

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleInsertTable} variant="contained">Insert</Button>
        </DialogActions>
      </Dialog>

      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        sx={{
          '& .MuiPaper-root': {
            minWidth: 180,
            boxShadow: '0px 5px 15px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'divider'
          }
        }}
      >
        <MenuItem onClick={() => handleTableAction('insert-row-above')}>
          <ListItemIcon>
            <VerticalAlignTopOutlined style={{ fontSize: '16px' }} />
          </ListItemIcon>
          <ListItemText primary="Insert Row Above" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>
        <MenuItem onClick={() => handleTableAction('insert-row-below')}>
          <ListItemIcon>
            <VerticalAlignBottomOutlined style={{ fontSize: '16px' }} />
          </ListItemIcon>
          <ListItemText primary="Insert Row Below" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleTableAction('insert-column-left')}>
          <ListItemIcon>
            <ArrowLeftOutlined style={{ fontSize: '16px' }} />
          </ListItemIcon>
          <ListItemText primary="Insert Column Left" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>
        <MenuItem onClick={() => handleTableAction('insert-column-right')}>
          <ListItemIcon>
            <ArrowRightOutlined style={{ fontSize: '16px' }} />
          </ListItemIcon>
          <ListItemText primary="Insert Column Right" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleTableAction('delete-row')} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteOutlined style={{ fontSize: '16px', color: 'red' }} />
          </ListItemIcon>
          <ListItemText primary="Delete Row" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>
        <MenuItem onClick={() => handleTableAction('delete-column')} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteOutlined style={{ fontSize: '16px', color: 'red' }} />
          </ListItemIcon>
          <ListItemText primary="Delete Column" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleTableAction('delete-table')} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <TableOutlined style={{ fontSize: '16px', color: 'red' }} />
          </ListItemIcon>
          <ListItemText primary="Delete Table" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>
      </Menu>
    </Box >
  );
});

export default QuillEditor;
